import React, { useState, useRef } from 'react';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { exportToPDF } from '../../../utils/exportUtils';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { useDashboard } from '../hooks/useDashboard';
import { formatCurrency } from '../../../components/common';
import FilterSection from '../../../components/ui/FilterSection';

// Sub-components
import { DashboardBanner } from '../components/DashboardBanner';
import { MetricsGrid } from '../components/MetricsGrid';
import { ChartsSection } from '../components/ChartsSection';
import { PipelineChartsSection } from '../components/PipelineChartsSection';
import { SalesPerformanceSection } from '../components/SalesPerformanceSection';
import { SalesTeamPerformanceSection } from '../components/SalesTeamPerformanceSection';
import { ActivitiesSection } from '../components/ActivitiesSection';
import { DashboardProjectSearchTable } from '../components/DashboardProjectSearchTable';
import { mockSalesTeam } from '../../../store/mockData';

// Register ChartJS modules globally
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

export const DashboardPage = () => {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: 'all', fromDate: '', toDate: '' });

  const {
    user,
    analytics,
    revenueChartData,
    leadSourceData,
    monthlyLeadsData,
    monthlySalesBarData,
    recentLeads,
    activeProjects,
    allLeads,
    allProjects,
    salesTeam,
    loading
  } = useDashboard(search, filters);

  const [isExporting, setIsExporting] = useState(false);
  const page1Ref = useRef(null);
  const page2Ref = useRef(null);

  const handleExport = async () => {
    if (loading || isExporting || !page1Ref.current || !page2Ref.current) return;
    try {
      setIsExporting(true);
      
      const pad = n => String(n).padStart(2, '0');
      const d = new Date();
      const dateStr = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
      
      await exportToPDF([page1Ref.current, page2Ref.current], `Dashboard_Export_${dateStr}.pdf`);
      toast.success('Dashboard exported to PDF successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export dashboard PDF');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-100px)] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-800"></div>
          <p className="text-sm font-medium text-slate-500 animate-pulse">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-12 overflow-x-hidden">
      
      {/* 1. Custom Welcome Row Panel */}
      <DashboardBanner user={user} />

      <div className="flex w-full mt-[-10px]">
        <div className="flex-1">
          <FilterSection 
            search={search}
            setSearch={setSearch}
            filters={filters}
            setFilters={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
            statusOptions={[]} 
            hideFilterButton={true} 
            customActions={
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="relative flex items-center gap-2 bg-white text-slate-700 px-4 py-2 rounded-lg border border-slate-200 shadow-sm hover:bg-slate-50 hover:shadow hover:text-brand-blue transition-all duration-300 font-medium text-sm"
              >
                {isExporting ? (
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-brand-blue rounded-full animate-spin" />
                ) : (
                  <Download size={18} />
                )}
                <span>{isExporting ? 'Exporting PDF...' : 'Export to PDF'}</span>
              </button>
            }
          />
        </div>
      </div>


      <div className="flex flex-col gap-6 w-full">
        {/* Page 1 Export Section */}
        <div ref={page1Ref} className="flex flex-col gap-6 bg-slate-50/50 p-4 -m-4 rounded-xl">
          {/* 2. Operations Metric Grid Cards */}
          <MetricsGrid analytics={analytics} />

          {/* 8. Financial Summary (Moved Below Stats) */}
          <div className="card p-5 border border-gray-100 rounded-xl shadow-sm bg-white">
            <h3 className="font-serif text-lg text-slate-800 font-medium mb-4">Financial Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Projects Revenue', value: analytics.totalRevenue, color: 'text-slate-800', bg: 'bg-slate-50' },
                { label: 'Total Sales', value: analytics.totalRevenue, color: 'text-purple-600', bg: 'bg-purple-50' },
                { label: 'Total Expenses', value: analytics.totalExpenses, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Net profit', value: analytics.netProfit, color: analytics.netProfit >= 0 ? 'text-green-600' : 'text-red-600', bg: analytics.netProfit >= 0 ? 'bg-green-50' : 'bg-red-50' },
              ].map((stat, idx) => (
                <div key={idx} className={`rounded-xl p-5 ${stat.bg} flex flex-col items-center justify-center text-center transition-all duration-300 hover:scale-[1.02] border border-black/5`}>
                  <div className={`text-2xl font-bold font-serif ${stat.color} mb-1`}>
                    {formatCurrency(stat.value || 0)}
                  </div>
                  <div className="text-sm font-medium text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. High-Definition Charts Panel (Line + Bar) */}
          <ChartsSection revenueChartData={revenueChartData} opacity={1} leadSourceData={leadSourceData} monthlyLeadsData={monthlyLeadsData} />

          {/* 4. Pipeline Distribution Charts */}
          <PipelineChartsSection mockLeads={allLeads} mockProjects={allProjects} monthlySalesBarData={monthlySalesBarData} />

          {/* 6. Sales Team Performance */}
          <SalesTeamPerformanceSection salesTeam={analytics.salesTeam || []} />
        </div>

        {/* Page 2 Export Section */}
        <div ref={page2Ref} className="flex flex-col gap-6 bg-slate-50/50 p-4 -m-4 rounded-xl">
          {/* 7. Bottom Activity Panels */}
          <ActivitiesSection 
            recentLeads={recentLeads.filter(lead => {
              if (!search) return true;
              const id = (lead.customId || lead.projectId || lead.leadId || lead._id || '').toLowerCase();
              return id.includes(search.toLowerCase());
            })} 
            activeProjects={activeProjects.filter(project => {
              if (!search) return true;
              const id = (project.customId || project.projectId || project.projectNumber || project._id || '').toLowerCase();
              return id.includes(search.toLowerCase());
            })} 
          />
        </div>
      </div>

    </div>
  );
};
